import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Wallet, TrendingUp, CreditCard, Banknote, ArrowDownToLine, ShieldCheck } from "lucide-react";
import api, { formatTRY, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";
import Pagination from "@/components/Pagination";

const PAGE_SIZE = 50;

const METHODS = [
    { v: "", l: "Tümü" },
    { v: "cash", l: "Nakit" },
    { v: "bank_transfer", l: "Havale/EFT" },
    { v: "card", l: "Kredi Kartı" },
    { v: "stripe", l: "Stripe" },
    { v: "other", l: "Diğer" },
];

const METHOD_LABELS = {
    cash: "Nakit",
    bank_transfer: "Havale/EFT",
    card: "Kredi Kartı",
    stripe: "Stripe",
    other: "Diğer",
};

const METHOD_ICONS = {
    cash: Banknote,
    bank_transfer: ArrowDownToLine,
    card: CreditCard,
    stripe: ShieldCheck,
    other: Wallet,
};

export default function AdminFinancePage() {
    const navigate = useNavigate();
    const { user, loading } = useAuth() || {};

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [summary, setSummary] = useState({ by_method: {}, grand_total: 0 });
    const [q, setQ] = useState("");
    const [method, setMethod] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const load = useCallback(async () => {
        const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String((page - 1) * PAGE_SIZE) });
        if (q) params.set("q", q);
        if (method) params.set("method", method);
        if (fromDate) params.set("from_date", fromDate);
        if (toDate) params.set("to_date", toDate);
        const r = await api.get(`/admin/finance/payments?${params.toString()}`);
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
        setSummary(r.data.summary || { by_method: {}, grand_total: 0 });
    }, [q, method, fromDate, toDate, page]);

    useEffect(() => {
        if (loading) return;
        if (!user || !["super_admin", "admin", "finance"].includes(user.role)) {
            navigate("/login");
            return;
        }
        load();
    }, [user, loading, load, navigate]);

    // Filtre değişince sayfayı 1'e dön
    useEffect(() => { setPage(1); }, [q, method, fromDate, toDate]);

    return (
        <AdminLayout title="Finans">
            <div className="space-y-4">
                {/* Summary cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <SummaryCard
                        label="Genel Toplam"
                        value={formatTRY(summary.grand_total || 0)}
                        sub={`${total} işlem`}
                        accent
                        icon={<TrendingUp className="h-5 w-5" />}
                    />
                    {["cash", "bank_transfer", "card", "stripe"].map((m) => {
                        const Icon = METHOD_ICONS[m];
                        const s = summary.by_method?.[m] || { total: 0, count: 0 };
                        return (
                            <SummaryCard
                                key={m}
                                label={METHOD_LABELS[m]}
                                value={formatTRY(s.total)}
                                sub={`${s.count} işlem`}
                                icon={<Icon className="h-5 w-5" />}
                            />
                        );
                    })}
                </div>

                {/* Filters */}
                <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-2 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                            data-testid="finance-search"
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Otel adı, misafir, email..."
                            className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm"
                        />
                    </div>
                    <select data-testid="finance-method" value={method} onChange={(e) => setMethod(e.target.value)} className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm">
                        {METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                    <div className="grid grid-cols-2 gap-2">
                        <input type="date" data-testid="finance-from" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />
                        <input type="date" data-testid="finance-to" value={toDate} onChange={(e) => setToDate(e.target.value)} className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />
                    </div>
                </div>

                {/* List */}
                <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Para İşlemleri ({total})</div>
                        <span className="text-[10px] text-slate-500">Audit: işlemler silinemez</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 dark:border-slate-800">
                                    <th className="px-4 py-3">Tarih</th>
                                    <th className="px-4 py-3">PNR / Otel</th>
                                    <th className="px-4 py-3">Misafir</th>
                                    <th className="px-4 py-3">Yöntem</th>
                                    <th className="px-4 py-3 text-right">Tutar</th>
                                    <th className="px-4 py-3">Not</th>
                                    <th className="px-4 py-3">Operatör</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 && (
                                    <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">Filtreyle eşleşen para işlemi yok.</td></tr>
                                )}
                                {items.map((p) => (
                                    <tr key={p.payment_id} data-testid={`fin-row-${p.payment_id}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300 whitespace-nowrap">{p.date}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-mono text-xs font-bold text-sky-600 dark:text-sky-400">{p.pnr}</div>
                                            <div className="text-xs text-slate-500 truncate max-w-[200px]">{p.hotel_name}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-slate-900 dark:text-white">{p.guest_first_name} {p.guest_last_name}</div>
                                            <div className="text-[11px] text-slate-500">{p.guest_email}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <MethodBadge method={p.method} />
                                        </td>
                                        <td className="px-4 py-3 text-right font-bold text-emerald-600 dark:text-emerald-400">{formatTRY(p.amount)}</td>
                                        <td className="px-4 py-3 text-xs text-slate-500 max-w-[200px] truncate">{p.note || "-"}</td>
                                        <td className="px-4 py-3 text-xs text-slate-500">{p.created_by || "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}

function SummaryCard({ label, value, sub, accent, icon }) {
    return (
        <div className={`rounded-2xl p-4 border ${accent ? "bg-gradient-to-br from-sky-500 to-indigo-600 text-white border-sky-500" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"}`}>
            <div className="flex items-center gap-2 mb-1">
                <span className={accent ? "text-white/80" : "text-sky-500"}>{icon}</span>
                <span className={`text-[10px] font-bold uppercase tracking-widest ${accent ? "text-white/80" : "text-slate-500"}`}>{label}</span>
            </div>
            <div className={`text-lg font-display font-bold ${accent ? "text-white" : "text-slate-900 dark:text-white"}`}>{value}</div>
            <div className={`text-[11px] ${accent ? "text-white/80" : "text-slate-500"}`}>{sub}</div>
        </div>
    );
}

function MethodBadge({ method }) {
    const colors = {
        cash: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
        bank_transfer: "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300",
        card: "bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
        stripe: "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300",
        other: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    };
    return <span className={`text-[10px] font-bold px-2 py-1 rounded-md inline-block ${colors[method] || colors.other}`}>{METHOD_LABELS[method] || method}</span>;
}
